import React, { useEffect, useMemo, useState } from 'react';
import {
  AppButton,
  Box,
  RoundPendingIcon,
  ServiceConfig,
  Text,
} from '@common-fe/common-fe';

import { ModalWrapper } from '@/components';
import { useHealthPlanNameStore } from '@/modules/HealthPlan/HealthPlanSetup/BasicProperties/HealthPlanName/stores';
import { useHasUnselectedServiceTypes } from '@/modules/HealthPlan/HealthPlanSetup/hooks';

import { useEditModeStore } from '../stores';

import { useActiveHealthPlanUpdateModalStore } from './stores';

interface Props {
  onUpdate: () => void;
  onActivate?: () => void;
  isTemplateMode?: boolean;
  isPlanNotActive?: boolean;
  serviceConfigs: ServiceConfig[];
}

export const ActiveHealthPlanUpdateModal: React.FC<Props> = ({ onUpdate, onActivate, isTemplateMode, isPlanNotActive, serviceConfigs }) => {
  const [isServiceWarnMode, setServiceWarnMode] = useState(false);
  const visible = useActiveHealthPlanUpdateModalStore((store) => store.modalVisible);
  const handleSetModalState = useActiveHealthPlanUpdateModalStore((store) => store.handleSetState);
  const healthPlanName = useActiveHealthPlanUpdateModalStore((store) => store.healthPlanName);
  const healthPlanTypes = useHealthPlanNameStore((state) => state.state.healthPlanTypes);
  const handleResetStore = useActiveHealthPlanUpdateModalStore((store) => store.handleReset);
  const setEditMode = useEditModeStore((state) => state.setEditMode);
  const { hasUnselectedServiceTypes, unselectedServiceTypes } = useHasUnselectedServiceTypes(serviceConfigs, healthPlanTypes);
  const unselectedServiceTypesText = useMemo(() => unselectedServiceTypes.join(', '), [unselectedServiceTypes]);
  const headerPlanText = useMemo(
    () =>
      hasUnselectedServiceTypes && isServiceWarnMode
        ? 'Are you sure about your setup?'
        : 'Would you like to save changes?',
    [hasUnselectedServiceTypes, isServiceWarnMode]
  );

  const subPlanText = useMemo(
    () =>
      hasUnselectedServiceTypes && isServiceWarnMode
        ? `Service payments for the ${unselectedServiceTypesText} ${unselectedServiceTypes?.length > 1 ? 'categories' : 'category'} will be deleted because they don’t match the health plan type.`
        : 'Any changes made to deductible tier amounts may impact the tier settings of currently enrolled participants. Previously processed payments will not be impacted.',
    [hasUnselectedServiceTypes, isServiceWarnMode, unselectedServiceTypes?.length, unselectedServiceTypesText]
  );

  const headerText = useMemo(() => {
    if (isTemplateMode) {
      return `Update ${healthPlanName} template?`;
    }
    return headerPlanText;
  }, [headerPlanText, healthPlanName, isTemplateMode]);

  const subText = useMemo(() => {
    if (isTemplateMode) {
      return `Updates made here will not reflect in any draft or active health plans created from this template.
        To update health plan templates, please make updates directly in the health plan template.`;
    }
    return subPlanText;
  }, [isTemplateMode, subPlanText]);

  const handleUpdateClick = React.useCallback(() => {
    if (isPlanNotActive && onActivate) {
      handleSetModalState({ modalVisible: false });
      onActivate();
    } else if (isServiceWarnMode) {
      setServiceWarnMode(false);
    } else {
      onUpdate();
      setEditMode(false);
    }
  }, [isPlanNotActive, onActivate, isServiceWarnMode, handleSetModalState, onUpdate, setEditMode]);

  useEffect(() => {
    if (hasUnselectedServiceTypes && !isTemplateMode && visible) {
      setServiceWarnMode(true);
    } else {
      setServiceWarnMode(false);
    }
  }, [hasUnselectedServiceTypes, isTemplateMode, visible]);

  return (
    <ModalWrapper
      testId="ActiveHealthPlanUpdateModal_modal_wrapper"
      visible={visible}
      onSetVisible={handleResetStore}
    >
      <Box align="center" pad={{  vertical: 'spacing24' }}>
        <RoundPendingIcon size="48px" color="warning" />
        <Text
          data-testid="ActiveHealthPlanUpdateModal_modal_header"
          margin={{ vertical: 's', bottom: 's' }}
          textAlign="center"
          size="24px"
          weight={700}
          color="textBody"
        >
          {headerText}
        </Text>
        <Box margin={{ bottom: 'l' }} >
          <Text weight={400} textAlign="center" size="medium">
            {subText}
          </Text>
        </Box>
        <Box
          direction="row"
          gap="spacing12"
        >
          <AppButton
            buttonType="secondary"
            testId="ActiveHealthPlanUpdateModal_modal_cancel"
            onClick={handleResetStore}
            width="150px"
          >
            Cancel
          </AppButton>
          <AppButton
            testId="ActiveHealthPlanUpdateModal_modal_deactivate"
            onClick={handleUpdateClick}
            width="150px"
          >
            {isServiceWarnMode ? 'Proceed' : 'Update'}
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};
